/*
 * Initializes Swiper on every .dc-hero__media-viewport element — the image
 * carousel only. Swiper itself is installed as a theme asset
 * (swiper-bundle.min.js) and loaded by sections/dc-hero-banner.liquid before
 * this file, so window.Swiper is already available here.
 *
 * On desktop the copy column (.dc-hero__content--desktop) lives outside the
 * swiper viewport and doesn't slide with the image — it crossfades via the
 * "is-active" class, kept in sync with the active slide on every
 * slideChange. This is deliberate: the pagination dots sit in a fixed screen
 * position, so if copy slid along with the image underneath them, the dots
 * would visually cross the image mid-swipe.
 *
 * Swiper still owns slide switching, autoplay, touch/swipe and pagination —
 * bullets on desktop, a progress bar on mobile (see the breakpoint below). To
 * pin a different starting slide, use the section's "Initial slide" setting
 * (read here from data-initial-slide) rather than editing markup.
 *
 * On mobile the pagination row also sits outside the swiper viewport (so it
 * stays a single shared element instead of duplicating per slide), and needs
 * to land flush on the image's bottom edge. CSS alone couldn't do that
 * reliably: .dc-hero's own height is only a flex-stretch stand-in for the
 * image's real rendered height (fed by Swiper's internal measurement of the
 * viewport), not guaranteed pixel-identical to it, so a bottom:0 (or
 * equivalent) offset kept landing a few px past the image. syncMobilePagination
 * measures the viewport's *actual* rendered height directly and positions the
 * row from that real number instead.
 */
const MOBILE_PAGINATION_BAR_HEIGHT = 4; // matches .swiper-pagination-progressbar height in CSS
const MOBILE_BREAKPOINT = 768;

function syncMobilePagination(root, viewport) {
  const paginationRow = root.querySelector('.dc-hero__pagination-row');
  if (!paginationRow) return () => {};

  function update() {
    if (window.innerWidth > MOBILE_BREAKPOINT) {
      paginationRow.style.top = '';
      return;
    }
    paginationRow.style.top = `${viewport.offsetHeight - MOBILE_PAGINATION_BAR_HEIGHT}px`;
  }

  update();
  window.addEventListener('resize', update);
  return update;
}

function initHero(viewport) {
  const root = viewport.closest('.dc-hero') || viewport;
  const slideCount = viewport.querySelectorAll('.swiper-slide').length;
  const initialSlide = Number(viewport.dataset.initialSlide) || 0;
  const desktopPanels = root.querySelectorAll('.dc-hero__content--desktop');
  const paginationEl = root.querySelector('.swiper-pagination');

  function syncDesktopCopy(activeIndex) {
    desktopPanels.forEach((panel) => {
      panel.classList.toggle('is-active', Number(panel.dataset.slideIndex) === activeIndex);
    });
  }

  const updateMobilePaginationPosition = syncMobilePagination(root, viewport);

  const swiper = new window.Swiper(viewport, {
    slidesPerView: 1,
    loop: false,
    speed: 500,
    initialSlide,
    autoplay: slideCount > 1 ? { delay: 6000, disableOnInteraction: false } : false,
    pagination: paginationEl
      ? {
          el: paginationEl,
          type: 'progressbar',
          clickable: true,
        }
      : false,
    breakpoints: {
      768: {
        pagination: { type: 'bullets' },
      },
    },
    on: {
      init() {
        updateMobilePaginationPosition();
      },
      resize() {
        updateMobilePaginationPosition();
      },
      slideChange(instance) {
        updateMobilePaginationPosition();
        syncDesktopCopy(instance.activeIndex);
      },
    },
  });

  syncDesktopCopy(swiper.activeIndex);
  return swiper;
}

function ready(fn) {
  if (document.readyState !== 'loading') fn();
  else document.addEventListener('DOMContentLoaded', fn);
}

ready(() => {
  document.querySelectorAll('.dc-hero__media-viewport.swiper').forEach(initHero);
});
